import { UrlObject } from 'url'
import { assertNever } from '@omnea-app/legacy/utils/type-guard/assertNever'
import { RequestTopLevelFilterKey } from '@omnea-app/requests/requestsFrontendClient'
import type { GridRowId } from '@mui/x-data-grid-premium'

export const generateRequestsListRoute = (mode?: RequestTopLevelFilterKey) => {
  if (!mode) {
    // The requests page will determine the tab based on local storage or user's role
    return '/requests'
  }
  switch (mode) {
    case 'mine':
      return '/requests/mine'
    case 'all':
      return '/requests/all'
    case 'pending':
      return '/requests/pending'
    case 'minePending':
      return '/requests/my-pending'
    case 'delayed':
      return '/requests/delayed'
    case 'mineDelayed':
      return '/requests/my-delayed'
    case 'completed':
      return '/requests/completed'
    case 'mineCompleted':
      return '/requests/my-completed'
    case 'cancelled':
      return '/requests/cancelled'
    case 'mineCancelled':
      return '/requests/my-cancelled'
    case 'paused':
      return '/requests/paused'
    case 'minePaused':
      return '/requests/my-paused'
    case 'rejected':
      return '/requests/rejected'
    case 'following':
      return '/requests/following'
    case 'owned':
      return '/requests/owned'
    case 'awaiting-receipt':
      return '/requests/awaiting-receipt'
    case 'rejected-tasks':
      return '/requests/rejected-tasks'
    case 'errored-tasks':
      return '/requests/errored-tasks'
    default: {
      assertNever(mode)
      return `/requests/${mode}`
    }
  }
}

export const generateRequestDetailRoute = (tab: RequestTabs, requestId: string | GridRowId, taskId?: string, formId?: string) => {
  switch (tab) {
    case RequestTabs.OVERVIEW:
      return `/requests/detail/${requestId}/overview`
    case RequestTabs.INTAKE_FORM:
      return `/requests/detail/${requestId}/intake-form`
    case RequestTabs.SOURCE:
      return `/requests/detail/${requestId}/source`
    case RequestTabs.SUBMISSIONS:
      return `/requests/detail/${requestId}/submissions${formId ? `/${formId}` : ''}`
    case RequestTabs.RENEWAL_ASSESSMENT_FORM:
      return `/requests/detail/${requestId}/renewal-assessment`
    case RequestTabs.MY_TASKS:
      return `/requests/detail/${requestId}/my-tasks${taskId ? `/${taskId}` : ''}`
    case RequestTabs.DOCUMENTS:
      return `/requests/detail/${requestId}/documents`
    case RequestTabs.RISKS:
      return `/requests/detail/${requestId}/risks`
    case RequestTabs.RFX:
      return `/requests/detail/${requestId}/rfx`
    case RequestTabs.AUCTIONS:
      return `/requests/detail/${requestId}/auctions`
    case RequestTabs.WORKFLOW_SNAPSHOT:
      return `/requests/detail/${requestId}/workflow`
    case RequestTabs.LINE_ITEMS:
      return `/requests/detail/${requestId}/line-items`
    default: {
      assertNever(tab)
      throw new Error(`Unhandled tab: ${tab}`)
    }
  }
}

export const rfxSections = ['activity', 'settings', 'suppliers', 'questionnaires', 'qna', 'responses', 'results'] as const
export type RfxSectionType = (typeof rfxSections)[number]

export const generateRfxSectionRoute = (sectionType: RfxSectionType, requestId: string, round: number | undefined): UrlObject => {
  return {
    pathname: `/requests/detail/${requestId}/rfx/${sectionType}`,
    query: round !== undefined ? { round } : {},
  }
}

export const generateRfxSettingsSubRoute = (
  sub: 'deadlines' | 'ai-scoring',
  requestId: string,
  round: number | undefined
): UrlObject => {
  const settingsRoute = generateRfxSectionRoute('settings', requestId, round)
  return {
    ...settingsRoute,
    pathname: `${settingsRoute.pathname}/${sub}`,
  }
}

export const generateRfxResultsBreakdownRoute = ({
  requestId,
  round,
  requestFormTemplateId,
}: {
  requestId: string
  round: number | undefined
  requestFormTemplateId?: string
}): UrlObject => {
  const resultsRoute = generateRfxSectionRoute('results', requestId, round)
  return {
    ...resultsRoute,
    pathname: `${resultsRoute.pathname}/results-breakdown${requestFormTemplateId ? `/${requestFormTemplateId}` : ''}`,
  }
}

export const generateRfxResponsesFormRoute = ({
  requestId,
  round,
  requestFormTemplateId,
}: {
  requestId: string
  round: number | undefined
  requestFormTemplateId: string
}): UrlObject => {
  const responsesRoute = generateRfxSectionRoute('responses', requestId, round)
  return {
    ...responsesRoute,
    pathname: `${responsesRoute.pathname}/${requestFormTemplateId}`,
  }
}

export const generateRfxResponsesSupplierFormsRoute = ({
  requestId,
  round,
  requestFormTemplateId,
}: {
  requestId: string
  round: number | undefined
  requestFormTemplateId: string
}): UrlObject => {
  const formRoute = generateRfxResponsesFormRoute({ requestId, round, requestFormTemplateId })
  return {
    ...formRoute,
    pathname: `${formRoute.pathname}/supplier-forms`,
  }
}

export const generateRfxResponsesScoringRoute = (requestId: string, round: number | undefined, taskId: string): UrlObject => {
  const responsesRoute = generateRfxSectionRoute('responses', requestId, round)
  return {
    ...responsesRoute,
    pathname: `${responsesRoute.pathname}/scoring/${taskId}`,
  }
}

export const generateRfxPricingAnalysisRoute = ({
  requestId,
  round,
  requestFormTemplateId,
}: {
  requestId: string
  round: number | undefined
  requestFormTemplateId: string
}): UrlObject => {
  const responsesFormRoute = generateRfxResponsesFormRoute({ requestId, round, requestFormTemplateId })
  return {
    ...responsesFormRoute,
    pathname: `${responsesFormRoute.pathname}/pricing-analysis`,
  }
}

export const requestDetailUrlTabs = [
  'overview',
  'my-tasks',
  'intake-form',
  'source',
  'submissions',
  'renewal-assessment',
  'documents',
  'risks',
  'rfx',
  'auctions',
  'workflow',
  'line-items',
] as const
export type RequestDetailUrlTab = (typeof requestDetailUrlTabs)[number]

export const parseRequestDetailRoute = (tab: RequestDetailUrlTab) => {
  switch (tab) {
    case 'overview':
      return RequestTabs.OVERVIEW
    case 'intake-form':
      return RequestTabs.INTAKE_FORM
    case 'source':
      return RequestTabs.SOURCE
    case 'submissions':
      return RequestTabs.SUBMISSIONS
    case 'renewal-assessment':
      return RequestTabs.RENEWAL_ASSESSMENT_FORM
    case 'my-tasks':
      return RequestTabs.MY_TASKS
    case 'documents':
      return RequestTabs.DOCUMENTS
    case 'risks':
      return RequestTabs.RISKS
    case 'rfx':
      return RequestTabs.RFX
    case 'auctions':
      return RequestTabs.AUCTIONS
    case 'workflow':
      return RequestTabs.WORKFLOW_SNAPSHOT
    case 'line-items':
      return RequestTabs.LINE_ITEMS
    default: {
      assertNever(tab)
      return RequestTabs.OVERVIEW
    }
  }
}

export enum RequestTabs {
  OVERVIEW = 'OVERVIEW',
  INTAKE_FORM = 'INTAKE_FORM',
  SOURCE = 'SOURCE',
  RENEWAL_ASSESSMENT_FORM = 'RENEWAL_ASSESSMENT_FORM',
  SUBMISSIONS = 'SUBMISSIONS',
  MY_TASKS = 'MY_TASKS',
  DOCUMENTS = 'DOCUMENTS',
  RISKS = 'RISKS',
  RFX = 'RFX',
  AUCTIONS = 'AUCTIONS',
  WORKFLOW_SNAPSHOT = 'WORKFLOW_SNAPSHOT',
  LINE_ITEMS = 'LINE_ITEMS',
}
